import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Facility {
  id: string
  name: string
  facility_type: string
  building: string
  floor: number
  area_sqm: number
  capacity: number
  status: string
  manager: string
}

interface WorkOrder {
  id: string
  work_order_id: string
  title: string
  category: string
  priority: string
  status: string
  due_date: string
  assigned_to: string
}

export default function FacilitiesPage() {
  const [facilities, setFacilities] = useState<Facility[]>([])
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [facRes, woRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/facilities/'),
      fetch(API_BASE + '/facilities/work-orders'),
      fetch(API_BASE + '/facilities/statistics')
    ])
    if (facRes.ok) setFacilities((await facRes.json()).facilities || [])
    if (woRes.ok) setWorkOrders((await woRes.json()).work_orders || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': case 'completed': return 'bg-green-100 text-green-800'
      case 'maintenance': case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'open': case 'pending_parts': return 'bg-yellow-100 text-yellow-800'
      case 'renovation': case 'closed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Facilities Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Work Order</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Facilities</div>
              <div className="text-2xl font-bold">{statistics.total_facilities}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Operational</div>
              <div className="text-2xl font-bold text-green-600">{statistics.operational_facilities}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Work Orders</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.open_work_orders}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Critical Orders</div>
              <div className="text-2xl font-bold text-red-600">{statistics.critical_orders}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Area (sqm)</div>
              <div className="text-2xl font-bold">{statistics.total_area_sqm?.toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Facilities</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facility</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {facilities.slice(0, 12).map(fac => (
                  <tr key={fac.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{fac.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{fac.facility_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{fac.building}</div>
                      <div className="text-xs text-gray-500">Floor {fac.floor}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{fac.capacity} people</div>
                      <div className="text-xs text-gray-500">{fac.area_sqm} sqm</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(fac.status)}`}>{fac.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Work Orders</h3>
            <div className="space-y-3">
              {workOrders.filter(w => w.status !== 'completed').slice(0, 10).map(wo => (
                <div key={wo.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{wo.title}</span>
                    <span className={`text-xs ${getPriorityColor(wo.priority)}`}>{wo.priority}</span>
                  </div>
                  <div className="text-xs text-gray-500">{wo.work_order_id} - {wo.category}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(wo.status)}`}>{wo.status}</span>
                    <span className="text-xs text-gray-500">Due: {wo.due_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
